import { useEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import localStorage from 'local-storage';
import {
  BackButtonCWUI,
  BadgeUI,
  ButtonUI,
  CustomerWebsiteLayoutUI,
} from '@next-order/next-order-components';
import AdyenDropinPayment from 'components/OrderPayment/AdyenDropinPayment';
import getCardTypeFromName from 'helpers/getCardTypeFromName';
import { fetchOrder } from 'containers/Home/actions';
import { ORDER_PAYMENT_TYPE } from 'helpers/enums';
import { selectStoreConfig } from 'containers/Layout/selectors';
import getOrderObjectIfDinein from 'helpers/getOrderObjectIfDinein';
import { AdyenProvider } from 'components/OrderPayment/AdyenProvider';
import exclamationMark from '../../assets/images/exclamationmark.svg';
import { creditCardAdyenFetched } from 'containers/ReviewOrder/actions';
import PaymentErrorModal from 'components/OrderPayment/PaymentErrorModal';
import Layout from 'containers/Layout';
import getImage from 'helpers/getImage';
import ReviewOrder from 'containers/ReviewOrder';

const Checkout = () => {
  const dispatch = useDispatch();
  const dropinRef = useRef(null);
  const requestOrderObjAdyenRef = useRef(null);
  const storeConfig = useSelector(selectStoreConfig);

  const { search, pathname } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);
  const error = query.get('error');

  const adyenPaymentMethods = localStorage.get('adyenPaymentMethods');
  const adyenOrderObject = localStorage.get('adyenOrderObject');
  const toPay = localStorage.get('toPay');

  const { notes } = useSelector((state) => state.homeReducer);

  const [localState, setLocalState] = useReducer((s, a) => ({ ...s, ...a }), {
    error: !!error,
    isCardError: false,
    isError: false,
    errorMessage: '',
    currentOrder: adyenOrderObject?.order,
    paymentObj: adyenOrderObject?.payment,
  });

  const currentOrder = localState?.currentOrder;
  console.log('currentOrder:', currentOrder);

  useEffect(() => {
    if (!adyenPaymentMethods || !adyenOrderObject) {
      history.replace('/');
      return;
    }
    dispatch(creditCardAdyenFetched(adyenPaymentMethods));
    if (error) {
      history.replace(pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdyenClick = () => {
    if (!currentOrder) return;

    const currentOrderObj = { ...currentOrder };
    currentOrderObj.paymentType = ORDER_PAYMENT_TYPE.card_online;
    setLocalState({
      currentOrder: { ...currentOrderObj },
    });
  };

  const handleResetAdyen = () => {
    if (dropinRef.current) {
      dropinRef.current?.resetDropin();
    }
  };

  const fetchPlaceOrder = async (orderObj3d, isAppleGooglePay = false) => {
    const cloudFunction = isAppleGooglePay
      ? 'placeOrderApplePayV2'
      : 'placeOrder3DSecureV2';
    const response = await fetch(
      `${storeConfig.cloudFunctionsUrl}/${cloudFunction}`,
      {
        method: 'post',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...orderObj3d,
        }),
      }
    );
    const data = await response.json();
    return { ...data, isAppleGooglePay };
  };

  const placeOrderWithAdyen = async (
    currOrder = currentOrder,
    adyenData,
    isAppleGooglepay
  ) => {
    setLocalState({
      paymentInProcess: true,
      isError: false,
      errorMessage: '',
    });

    let orderObj = JSON.parse(JSON.stringify(currOrder));

    let requestObj = {
      order: { ...orderObj, ...{ notes } },
      payment: {
        ...localState?.paymentObj,
        ...adyenData,
      },
      toPay,
    };

    const orderObj3d = await getOrderObjectIfDinein(requestObj);
    const fetchOrder = await fetchPlaceOrder(orderObj3d, isAppleGooglepay);
    const orderId = fetchOrder?.orderId || '';
    requestOrderObjAdyenRef.current = { ...orderObj3d, orderId };
    return { ...fetchOrder, orderType: orderObj.orderType };
  };

  const onSubmit = async (state, component, actions) => {
    setLocalState({
      cardPaymentInProcess: true,
    });
    const stateData = state?.data || {};
    const { paymentMethod } = stateData;
    if (paymentMethod) {
      const cardType = paymentMethod?.brand
        ? getCardTypeFromName(paymentMethod?.brand)?.type
        : '';

      try {
        const type = paymentMethod?.type;
        const isAppleGooglePay = type === 'applepay' || type === 'googlepay';
        const storePaymentMethod = !(
          isAppleGooglePay || paymentMethod?.storedPaymentMethodId
        );
        const adyenData = {
          ...stateData,
          ...(storePaymentMethod && { storePaymentMethod }),
        };
        const latestCurrentOrder = { ...currentOrder };
        latestCurrentOrder.cardType = cardType;

        const data = await placeOrderWithAdyen(
          latestCurrentOrder,
          adyenData,
          isAppleGooglePay
        );

        if (isAppleGooglePay) {
          component.setStatus('success');
        }

        if (data?.payload?.action) {
          component.handleAction(data.payload.action);
        } else if (data?.orderId) {
          actions.resolve();
          dispatch(fetchOrder(data.orderId));
          history.replace(`/order-tracking/${data.orderId}/${data.orderType}`);
        } else {
          actions.reject();
          actions.resolve();
          setLocalState({
            isCardError: true,
            isError: true,
            errorMessage: data?.message || 'Payment failed',
            paymentInProcess: false,
            cardPaymentInProcess: false,
          });
          component.setStatus('ready');
        }
      } catch (error) {
        actions.reject();
        setLocalState({
          isCardError: true,
          isError: true,
          errorMessage: 'An error occurred during payment',
          paymentInProcess: false,
          cardPaymentInProcess: false,
        });
        component.setStatus('ready');
      }
    }
  };

  const onAdditionalDetails = async (state, component, actions) => {
    try {
      if (state.data) {
        component.setStatus('loading');

        const response = await fetch(
          `${storeConfig.cloudFunctionsUrl}/adyenPaymentDetails`,
          {
            method: 'post',
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...state.data,
              storeId: storeConfig.storeId,
            }),
          }
        );
        const data = await response.json();

        actions.resolve();

        if (data.success) {
          component.setStatus('success');

          const requestOrderObjAdyen = requestOrderObjAdyenRef?.current;
          const orderId = requestOrderObjAdyen?.orderId;
          const orderType = requestOrderObjAdyen?.order?.orderType;

          dispatch(fetchOrder(orderId));

          history.replace(`/order-tracking/${orderId}/${orderType}`);
        } else {
          actions.reject();
          setLocalState({
            isCardError: true,
            isError: true,
            errorMessage: data?.message || 'Payment failed',
            paymentInProcess: false,
            cardPaymentInProcess: false,
          });
          component.setStatus('ready');
        }
      }
    } catch (error) {
      actions.reject();
      setLocalState({
        isCardError: true,
        isError: true,
        errorMessage: 'An error occurred during payment',
        paymentInProcess: false,
        cardPaymentInProcess: false,
      });
      component.setStatus('ready');
    }
  };

  const onPaymentCompleted = (result) => {
    console.log('onPaymentCompleted: ', result);
  };

  const onFailedorError = (from, data) => {
    console.error(from, data);
    setLocalState({
      isCardError: true,
      isError: true,
      errorMessage: 'An error occurred during payment',
      paymentInProcess: false,
      cardPaymentInProcess: false,
    });
  };

  const onPaymentFailed = (result) => {
    onFailedorError('onPaymentFailed: ', result);
  };

  const onError = (error) => {
    onFailedorError('onError: ', error);
  };

  const getOrderBar = () => {
    return (
      <div className={classNames(`cash detailsConfirm detailFinal active`)}>
        <div
          className={`moveNext detailsConfirm detailFinal detailPayment active`}
        >
          <BackButtonCWUI
            onClick={() => {
              history.replace('/');
            }}
          />
          <div>
            <div className='detailsTitle' style={{ marginTop: 0 }}>
              Payment Methods
            </div>
            <AdyenDropinPayment
              onClick={handleAdyenClick}
              ref={dropinRef}
              onSubmit={onSubmit}
              onAdditionalDetails={onAdditionalDetails}
              onPaymentCompleted={onPaymentCompleted}
              onPaymentFailed={onPaymentFailed}
              onError={onError}
            />

            {!localState.isCardError &&
              localState.cardCharges &&
              currentOrder?.paymentType !== ORDER_PAYMENT_TYPE.cash && (
                <div className='flex justify-center'>
                  <BadgeUI
                    title={`${localState.cardChargesTitle}: ${storeConfig.currency}${localState.cardCharges}`}
                    color='primary'
                    variant='self-colored'
                    fontWeight='normal'
                    fontSize='sm'
                    extendedClassName='card-fee-buuble !text-black'
                    extendedInnerClassName='normal-case'
                  />
                </div>
              )}

            {localState.isError && (
              <div className='cardCommon flex flex-col items-start gap-y-2 p-4 mb-5 rounded-lg'>
                <div>
                  <img src={exclamationMark} alt='error' className='h-[20px]' />
                </div>
                <span className='redSpan text-[14px] font-medium'>
                  {localState.errorMessage ? `${localState.errorMessage}` : ''}
                </span>
                {localState.isCardError && (
                  <>
                    <span className='text-[14px]'>
                      Please double check your card details or try a different
                      payment method.
                    </span>
                    <span className='text-[14px]'>
                      If the issue persists, contact your card issuer and try
                      again later.
                    </span>
                    <ButtonUI
                      onClick={() => {
                        setLocalState({
                          isCardError: false,
                          isError: false,
                          errorMessage: '',
                        });
                        handleResetAdyen();
                      }}
                      btnType='reset'
                      size='lg'
                      extendedClassName='w-full !bg-black !border-black hover:text-white hover:opacity-90 mt-2 h-[42px] rounded-xl'
                    >
                      Retry Payment
                    </ButtonUI>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdyenProvider isAdyen={true} toPay={toPay || 0}>
      <Layout>
        <CustomerWebsiteLayoutUI
          orderBar={getOrderBar()}
          orderBarAnimation={'slideUp'}
          logoSrc={getImage(storeConfig.logoPath || storeConfig.logo)}
          logoAlt={storeConfig.name}
          openOrderBar={true}
          extendedClassName='overflow-hidden'
        >
          <ReviewOrder orderObj={currentOrder} />
          {!!localState.error && (
            <PaymentErrorModal
              closeModal={() => {
                setLocalState({
                  error: false,
                });
              }}
            />
          )}
        </CustomerWebsiteLayoutUI>
      </Layout>
    </AdyenProvider>
  );
};

export default Checkout;
